<ng-container *ngIf="!isHidden">
    <span
        class="text-secondary d-flex align-items-center mt-n3 mb-1 px-1"
        *ngIf="isSynchronizationOn"
        [attr.aria-busy]="isSynchronizing"
        role="status"
    >
        <ng-container>
            {{ lastSynchronization ? 'Updated ' : 'Loading ' }}
            {{ lastSynchronization | dateRelative | async }}
        </ng-container>
        <sb-pending-button-indicator
            class="text-info"
            [state]="isSynchronizationOn && isSynchronizing ? 'loading' : 'success'"
        ></sb-pending-button-indicator>
    </span>
    <div class="sb-widget-header" *ngIf="ownsHeaderText">
        <span>{{ context.headerText }}</span>
        <span class="sb-header-info">{{ headerInfo | async }}</span>
    </div>

    <app-sb-messages [messages]="msgs"></app-sb-messages>

    <div class="sb-ag-table-wrapper">
        <div *ngIf="isLoading">
            <div aria-busy="true" class="skeleton-loader skeleton-loader-table">
                Loading data for table
            </div>
        </div>

        <!-- Buttons above table -->
        <div
            class="ui-grid-row"
            style="text-align: end"
            *ngIf="(displayDropDownButton() || buttons) && rowData.length > 0"
        >
            <ng-container *ngTemplateOutlet="buttonsFragment"></ng-container>
        </div>

        <!-- Placeholder when there is no data present -->
        <div
            role="alert"
            class="info d-inline-block"
            *ngIf="rowData.length == 0 && !isLoading && noDataMessage"
        >
            <strong>No transactions found!</strong>
            &nbsp; Please try and adjust period or filters if applied, or contact
            <a href="mailto:clientservice@swimbird.se">clientservice@swimbird.se</a>
            if you need assistance.
        </div>

        <div *ngIf="rowData.length > 0 && context">
            <ng-container *ngTemplateOutlet="tableFragment"></ng-container>
        </div>

        <!-- Redirect link (should probably be located somewhere else) -->
        <p
            *ngIf="context && context.redirectOptions"
            style="padding: 7px; color: #22606c; font-weight: bold"
        >
            <a
                class="sb-privateEquity-link"
                [routerLink]="['/' + context.redirectOptions.routingConfig.targetUrl]"
                routerLinkActive="router-link-active"
            >
                {{ context.redirectOptions.redirectText }}
                <tabler-icon name="arrow-right" class="ms-1"></tabler-icon>
            </a>
        </p>
    </div>

    <!-- Reusable markup fragments -->
    <ng-template #tableFragment>
        <ag-grid-angular
            *ngIf="rowData.length > 0"
            #agGrid
            class="ag-theme-material"
            [animateRows]="true"
            [rowData]="rowData"
            [columnDefs]="columnDefs"
            [editType]="editType"
            [defaultColDef]="defaultColDef"
            [headerHeight]="28"
            [sideBar]="sideBar"
            [columnTypes]="columnTypes"
            [pagination]="paginated"
            [suppressNoRowsOverlay]="true"
            [paginationAutoPageSize]="false"
            [paginationPageSize]="pageSize"
            (gridReady)="onGridReady($event)"
            (rowDoubleClicked)="onRowDoubleClicked($event)"
            (modelUpdated)="onModelUpdated()"
            (rowSelected)="onRowSelected($event)"
            [gridOptions]="gridOptions"
            [getContextMenuItems]="getContextMenuItems"
            [getMainMenuItems]="getMainMenuItems"
            (rowClicked)="onRowClicked()"
            [enterMovesDownAfterEdit]="true"
            [groupRowAggNodes]="groupAddGroupNodesFunc"
            [ngClass]="isCondensed ? 'sb-ag-grid-condensed' : ''"
            [autoGroupColumnDef]="autoGroupColumnDef"
            [detailCellRenderer]="detailCellRenderer"
            [frameworkComponents]="frameworkComponents"
            [detailRowAutoHeight]="true"
            [masterDetail]="masterDetail"
            [detailCellRendererParams]="detailCellRendererParams"
        ></ag-grid-angular>
    </ng-template>

    <ng-template #buttonsFragment>
        <!-- Custom buttons -->
        <div *ngIf="buttons || displayDropDownButton()" class="sb-table-toolbar-wrapper">
            <div *ngIf="buttons">
                <button
                    mat-button
                    *ngFor="let button of buttons"
                    (click)="buttonClicked(button.routingConfig.targetUrl)"
                >
                    <mat-icon *ngIf="button.icon">{{ button.icon }}</mat-icon>
                    <span>{{ button.label }}</span>
                </button>
            </div>
            <ng-container *ngIf="displayDropDownButton(); else exportContextMenu">
                <button
                    type="button"
                    class="btn btn-link text-primary"
                    [matMenuTriggerFor]="menu"
                    aria-label="Table options"
                >
                    <tabler-icon name="dots"></tabler-icon>
                </button>
                <mat-menu #menu="matMenu">
                    <ng-template [ngIf]="showAdvancedToolbar">
                        <button
                            type="button"
                            mat-menu-item
                            (click)="onCondensed($event)"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="layout-grid"
                            />
                            <span>View compact</span>
                        </button>
                        <button
                            type="button"
                            mat-menu-item
                            (click)="onAutoHeight($event)"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="arrow-autofit-height"
                            />
                            <span>Auto height</span>
                        </button>
                        <button
                            type="button"
                            mat-menu-item
                            (click)="onAutoSizeAll($event)"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="arrow-autofit-width"
                            />
                            <span>Auto size column</span>
                        </button>
                        <button
                            type="button"
                            mat-menu-item
                            (click)="onShrinkColumnsToFit($event)"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="arrow-autofit-content"
                            />
                            <span>Fit column size</span>
                        </button>
                        <button
                            mat-menu-item
                            type="button"
                            (click)="onResetLayout($event)"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="restore"
                            />
                            <span>Reset layout</span>
                        </button>
                    </ng-template>
                    <ng-template [ngIf]="!context.suppressExcelExport">
                        <button
                            type="button"
                            mat-menu-item
                            (click)="exportExcel()"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="file-type-xls"
                            />
                            <span>Download (.xls)</span>
                        </button>
                        <button
                            type="button"
                            mat-menu-item
                            (click)="exportCsv()"
                            class="d-inline-flex align-items-center"
                        >
                            <tabler-icon
                                class="me-2 sb-icon-md d-inline-flex"
                                name="file-type-csv"
                            />
                            <span>Download (.csv)</span>
                        </button>
                    </ng-template>
                </mat-menu>
            </ng-container>
        </div>
    </ng-template>
    <ng-template #exportContextMenu>
        <!-- only show explicitly for certain contexts (active routes)-->
        <ng-container
            *ngIf="
                currentContext === 'portfolio-report' ||
                currentContext === 'portfolio-summary'
            "
        >
            <button
                type="button"
                class="btn btn-link text-primary"
                [matMenuTriggerFor]="menu"
                aria-label="Table options"
            >
                <tabler-icon name="dots"></tabler-icon>
            </button>
            <mat-menu #menu="matMenu">
                <button
                    type="button"
                    mat-menu-item
                    (click)="exportExcel()"
                    class="d-inline-flex align-items-center"
                >
                    <tabler-icon
                        class="me-2 sb-icon-md d-inline-flex"
                        name="file-type-xls"
                    />
                    <span>Download (.xls)</span>
                </button>
                <button
                    type="button"
                    mat-menu-item
                    (click)="exportCsv()"
                    class="d-inline-flex align-items-center"
                >
                    <tabler-icon
                        class="me-2 sb-icon-md d-inline-flex"
                        name="file-type-csv"
                    />
                    <span>Download (.csv)</span>
                </button>
            </mat-menu>
        </ng-container>
    </ng-template>
</ng-container>
